import { useEffect } from 'react'
import { setUnreadTitleCount } from '../../helpers/documentTitle'
import { setFaviconBadge } from '../../helpers/faviconBadge'
import { bindMessageSoundUnlock } from '../../helpers/messageSound'

// Declared rather than aliased from PropsFromRedux: with a single prop and no
// actions, going through the connector's inferred type makes the component's props
// depend on the component, which TypeScript resolves as `any`.
export interface IProps {
  unreadCount: number
}

/**
 * Renders nothing — it exists to project the unread count onto the three surfaces
 * outside the React tree: the tab title, the favicon, and the OS app icon.
 *
 * One component rather than three hooks scattered around, because all three read
 * the same number and must clear together. Mounted once at the app root so the
 * count survives navigation.
 */
const UnreadBadge = ({ unreadCount }: IProps): null => {
  useEffect(() => bindMessageSoundUnlock(), [])

  useEffect(() => {
    setUnreadTitleCount(unreadCount)
    setFaviconBadge(unreadCount)

    // Installed-app icon badge. Absent in a plain browser tab (there is no app icon
    // to badge) and in Firefox, so it's feature-detected and simply skipped —
    // the title and favicon above are the surfaces that always work.
    if ('setAppBadge' in navigator) {
      const badgeNavigator = navigator as Navigator & {
        setAppBadge: (count?: number) => Promise<void>
        clearAppBadge: () => Promise<void>
      }
      const applied =
        unreadCount > 0 ? badgeNavigator.setAppBadge(unreadCount) : badgeNavigator.clearAppBadge()
      // Rejects when the document isn't in a badge-capable context; nothing to do.
      applied.catch((): void => {})
    }
  }, [unreadCount])

  return null
}

export default UnreadBadge
